import { ArrowUpRight, Hotel, Car, Plane } from "lucide-react";
import { useReveal } from "../hooks/useReveal";
import { buildWhatsAppLink } from "../lib/i18n";

const IMAGES = {
  hotel:
    "https://images.pexels.com/photos/7942132/pexels-photo-7942132.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  car: "https://images.unsplash.com/photo-1769787301187-0fab290ba2f2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  flight:
    "https://images.pexels.com/photos/28776068/pexels-photo-28776068.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
};

const ICONS = [Hotel, Car, Plane];
const KEYS = ["hotel", "car", "flight"];

const buildMessage = (lang, name) => {
  if (lang === "fr") {
    return `Bonjour PRESTIGE KEY, je souhaite réserver le service "${name}". Pouvez-vous m'envoyer une proposition sur-mesure ?`;
  }
  return `Hello PRESTIGE KEY, I would like to book the "${name}" service. Could you send me a tailored proposal?`;
};

export const Services = ({ t, lang }) => {
  const headerRef = useReveal();

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-28 sm:py-40 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div ref={headerRef} className="reveal mb-20 max-w-3xl">
          <p className="text-[10px] uppercase tracking-[0.4em] text-white/50 mb-6">
            — {t.services.eyebrow} —
          </p>
          <h2
            data-testid="services-title"
            className="heading-display text-4xl sm:text-5xl md:text-6xl mb-6"
          >
            {t.services.title}
          </h2>
          <p className="text-white/55 text-base sm:text-lg max-w-xl">
            {t.services.sub}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
          {t.services.items.map((item, idx) => {
            const Icon = ICONS[idx];
            const imgKey = KEYS[idx];
            return (
              <ServiceCard
                key={item.name}
                idx={idx}
                item={item}
                Icon={Icon}
                img={IMAGES[imgKey]}
                reserveLabel={t.services.reserve}
                lang={lang}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
};

const ServiceCard = ({ idx, item, Icon, img, reserveLabel, lang }) => {
  const ref = useReveal({ threshold: 0.2 });
  return (
    <article
      ref={ref}
      data-testid={`service-card-${idx}`}
      className="service-card reveal relative group rounded-2xl overflow-hidden border border-white/10 bg-[#0a0a0a] aspect-[3/4] flex flex-col"
      style={{ transitionDelay: `${idx * 0.1}s` }}
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img src={img} alt={item.name} className="card-img w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/55 to-black/95" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col h-full p-7 sm:p-9">
        <div className="flex items-start justify-between mb-auto">
          <span className="text-[10px] uppercase tracking-[0.32em] text-white/60">
            {item.tag}
          </span>
          <Icon
            size={28}
            strokeWidth={1.25}
            className="text-white/85 transition-transform duration-500 group-hover:-translate-y-1"
          />
        </div>

        <div>
          <h3 className="heading-display text-3xl sm:text-4xl mb-3">{item.name}</h3>
          <p className="text-sm text-white/65 leading-relaxed mb-7 max-w-xs">
            {item.desc}
          </p>

          <a
            data-testid={`service-reserve-${idx}`}
            href={buildWhatsAppLink(buildMessage(lang, item.name))}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-white border-b border-white/40 pb-1 hover:border-white transition-colors"
          >
            {reserveLabel}
            <ArrowUpRight
              size={14}
              className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
            />
          </a>
        </div>
      </div>
    </article>
  );
};
